import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const FEATURES_FREE = [
  "Wax-Up Fundamentals track",
  "Anterior Teeth Morphology track",
  "Full anatomy library (FDI)",
  "AI Tutor (Porcelain, Claude 4.5)",
  "Quizzes & progress tracking",
];

const FEATURES_PRO = [
  "Everything in Free",
  "Posterior Morphology (Advanced track)",
  "Occlusion & Function masterclass",
  "All future Pro tracks included",
  "Priority AI Tutor responses",
  "Lifetime access — one payment",
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paypalLoading, setPaypalLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/packages").then(({ data }) => setPackages(data));
  }, []);

  const pro = packages.find((p) => p.id === "masterclass_pro");

  const startCheckout = async () => {
    if (!user) {
      toast("Please sign in first to upgrade.");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "masterclass_pro",
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  const startPayPal = async () => {
    if (!user) {
      toast("Please sign in first to upgrade.");
      navigate("/login");
      return;
    }
    setPaypalLoading(true);
    try {
      const { data } = await api.post("/paypal/create-order", {
        package_id: "masterclass_pro",
        origin_url: window.location.origin,
      });
      if (data.approve_url) window.location.href = data.approve_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PayPal unavailable");
      setPaypalLoading(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-20" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="font-mono-label text-primary">Pricing</div>
        <h1 className="font-serif-display text-5xl md:text-6xl mt-3 font-light leading-none tracking-tighter">
          Free foundation. <em className="italic">Pro craftsmanship.</em>
        </h1>
        <p className="mt-6 text-muted-foreground">
          Start free and learn the fundamentals. Upgrade to Pro once for lifetime access to advanced posterior and occlusion masterclasses.
        </p>
      </div>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
        {/* Free tier */}
        <div className="porcelain-card p-8 flex flex-col" data-testid="pricing-free">
          <div className="font-mono-label text-muted-foreground">Foundation</div>
          <div className="font-serif-display text-5xl mt-3 leading-none">Free</div>
          <p className="mt-4 text-sm text-muted-foreground">For students learning the craft.</p>
          <ul className="mt-8 space-y-3 flex-1">
            {FEATURES_FREE.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <Check className="w-4 h-4 text-foreground mt-0.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <Link to={user ? "/courses" : "/signup"} className="btn-outline-mono mt-8 text-sm text-center" data-testid="pricing-free-cta">
            {user ? "Keep learning" : "Create free account"}
          </Link>
        </div>

        {/* Pro tier */}
        <div className="bg-foreground text-background p-8 flex flex-col relative overflow-hidden" style={{ borderRadius: 2 }} data-testid="pricing-pro">
          <div className="absolute top-4 right-4 font-mono-label text-primary bg-background px-2 py-1" style={{ borderRadius: 2 }}>RECOMMENDED</div>
          <div className="font-mono-label text-primary-foreground/70">Porcelain Pro</div>
          <div className="flex items-end gap-2 mt-3">
            <div className="font-serif-display text-6xl leading-none">${pro?.amount ?? 99}</div>
            <div className="font-mono-label text-primary-foreground/60 mb-3">LIFETIME · USD</div>
          </div>
          <p className="mt-4 text-sm text-background/70">{pro?.description || "Lifetime access to every premium track."}</p>
          <ul className="mt-8 space-y-3 flex-1">
            {FEATURES_PRO.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          {user?.is_premium ? (
            <div className="mt-8 btn-oxblood text-sm text-center opacity-70 cursor-default" data-testid="pricing-pro-current">You are Pro · Thank you</div>
          ) : (
            <div className="mt-8 space-y-3">
              <button
                onClick={startCheckout}
                disabled={loading}
                className="btn-oxblood w-full text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="pricing-pro-cta"
              >
                {loading ? "Redirecting…" : "Pay with card"} <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={startPayPal}
                disabled={paypalLoading}
                className="w-full text-sm inline-flex items-center justify-center gap-2 bg-[#FFC439] text-[#001F3F] py-3 disabled:opacity-50 hover:bg-[#F0B429] transition-colors"
                style={{ borderRadius: 2, fontWeight: 600 }}
                data-testid="pricing-paypal-cta"
              >
                {paypalLoading ? "Redirecting…" : "Pay with PayPal"}
              </button>
              <div className="font-mono-label text-primary-foreground/50 text-center text-[0.6rem]">SECURE CHECKOUT · STRIPE + PAYPAL</div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-16 border-t border-border pt-8 text-center text-xs font-mono-label text-muted-foreground">
        SECURE CHECKOUT BY STRIPE · 30-DAY SATISFACTION GUARANTEE
      </div>
    </div>
  );
}
